// src/redux/slices/setPasswordSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ResetPasswordAfterLogin } from "../../services/website/authService";
import createAxiosInstance, { axiosGet2 } from "../../axios/axiosMiddleware2";
import { API } from "../../apiEndPoints/website/apiEndPoints";

interface UserDetails {
  id: string;
  isPasswordSet: boolean;
  username?: string;
  email?: string;
  [key: string]: any;
}

interface RegisterFormValues {
  new_password: string;
  confirm_password: string;
}

interface ResetPasswordResponse {
  code: number;
  message?: string;
  message_key?: string;
}

interface AuthState {
  isPasswordModalOpen: boolean;
  userDetails: UserDetails | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isPasswordModalOpen: false,
  userDetails: null,
  isLoading: false,
  error: null,
};

export const fetchUserDetailsThunk = createAsyncThunk<UserDetails>(
  "setPassword/fetchUserDetails", // Slice name fix kiya
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const store = { getState, dispatch };
      const axiosInstance = createAxiosInstance(store);
      const response = await axiosGet2(axiosInstance, `${API?.OTHER?.GET_PROFILE_DETAILS}`);
      return response?.data?.data;
    } catch (error: any) {
      const messageKey =
        error?.response?.data?.message_key ||
        error?.data?.message_key ||
        "error_fetch_user_details";
      return rejectWithValue(messageKey);
    }
  }
);

export const resetPasswordThunk = createAsyncThunk<
  ResetPasswordResponse,
  { token: string; userId: string; data: RegisterFormValues },
  { rejectValue: string }
>(
  "setPassword/resetPassword", // Slice name fix kiya
  async ({  userId, data }, { rejectWithValue }) => {
    try {
      const response = await ResetPasswordAfterLogin( userId, {
        confirmPassword: data.confirm_password,
        newPassword: data.new_password,
      });
      return response?.data;
    } catch (error: any) {
      const messageKey =
        error?.response?.data?.message_key ||
        error?.data?.message_key ||
        "error_reset_password";
      return rejectWithValue(messageKey);
    }
  }
);

const setPasswordSlice = createSlice({
  name: "setPassword", // Slice name fix kiya
  initialState,
  reducers: {
    setPasswordModalOpen(state, action: PayloadAction<boolean>) {
      state.isPasswordModalOpen = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetailsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserDetailsThunk.fulfilled, (state, action: PayloadAction<UserDetails>) => {
        state.isLoading = false;
        state.userDetails = action.payload;
        state.isPasswordModalOpen = !action.payload.isPasswordSet;
      })
      .addCase(fetchUserDetailsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(resetPasswordThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPasswordThunk.fulfilled, (state, action: PayloadAction<ResetPasswordResponse>) => {
        state.isLoading = false;
        if (action.payload.code === 200) {
          state.isPasswordModalOpen = false;
          if (state.userDetails) {
            state.userDetails.isPasswordSet = true;
          }
        }
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPasswordModalOpen, clearError } = setPasswordSlice.actions;
export const setPasswordSelector = (state: { setPassword: AuthState }) => state.setPassword;
export default setPasswordSlice.reducer;